import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { ChevronDown, ChevronUp } from "lucide-react";

const Bookings = () => {
  // Utility functions for JST timezone
  const convertToJST = (date) => {
    // Add 9 hours for JST (UTC+9)
    return new Date(date.getTime() + (9 * 60 * 60 * 1000));
  };

  const formatDateInJST = (date) => {
    const jstDate = convertToJST(new Date(date));
    return {
      day: `${String(jstDate.getUTCDate()).padStart(2, "0")}-${String(
        jstDate.getUTCMonth() + 1
      ).padStart(2, "0")}`,
      fullDate: jstDate,
      year: jstDate.getUTCFullYear(),
      month: jstDate.getUTCMonth(),
      date: jstDate.getUTCDate(),
      hour: jstDate.getUTCHours(),
      minute: jstDate.getUTCMinutes()
    };
  };

  // Parse UTC timestamp to JST
  const parseUTCToJST = (timestamp) => {
    const utcDate = new Date(timestamp);
    return convertToJST(utcDate);
  };

  // Format JST date for display
  const formatJSTDateForDisplay = (date) => {
    const jstDate = convertToJST(new Date(date));
    return `${String(jstDate.getUTCDate()).padStart(2, '0')}-${String(jstDate.getUTCMonth() + 1).padStart(2, '0')}`;
  };

  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  };

  const [selectedFilter, setSelectedFilter] = useState("Daily");
  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedResource, setSelectedResource] = useState("All Resources");
  const [selectedDate, setSelectedDate] = useState(getYesterday());
  const [customStartDate, setCustomStartDate] = useState(getYesterday());
  const [customEndDate, setCustomEndDate] = useState(getYesterday());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [reportType, setReportType] = useState("daily");

  const ALL_RESOURCES = [
    "War Room",
    "Beijing Room",
    "Mumbai Room",
    "Shenzhen Room",
    "Auckland Room",
    "Shanghai Room",
    "Taipei Room",
    "Brisbane Room",
    "Melbourne Room",
    "Los Angeles Room",
    "Sydney Room",
    "Delhi Room",
    "London Room",
    "Tokyo Room",
    "Hong Kong Room",
    "Singapore Room",
    "New York Room",
    "Seoul Room",
  ];

  // API data state
  const [apiData, setApiData] = useState(null);
  const [data, setData] = useState({
    resources: [],
    totalAvailableHours: 0,
  });

  const transformApiData = (apiResponse) => {
    if (!apiResponse || !apiResponse.rooms) {
      // Return all resources with zero values if no API data
      return {
        resources: ALL_RESOURCES.map((name) => ({
          name: name,
          meetings: 0,
          meetingHours: "0h",
          occupancy: 0,
          cancelledMeetings: 0,
          cancelledHours: "0h",
          autoBookedMeetings: 0,
          autoBookingHours: "0h",
        })),
        totalAvailableHours: 0,
      };
    }

    // Transform API rooms data
    const transformedResources = apiResponse.rooms.map((room) => {
      console.log(`Transforming ${room.resourceName}:`, room);
      return {
        name: room.resourceName,
        meetings: room.number_of_meetings,
        meetingHours: room.meeting_hours,
        occupancy: Math.min(
          parseInt(room.booking_occupancy.replace("%", "")),
          100
        ),
        cancelledMeetings: room.cancelled_meetings,
        cancelledHours: room.cancelled_hours,
        autoBookedMeetings: room.auto_booked_meetings,
        autoBookingHours: room.auto_booking_hours,
      };
    });

    // Find missing resources (those not in API response)
    const apiResourceNames = transformedResources.map((r) => r.name);
    const missingResources = ALL_RESOURCES.filter(
      (name) => !apiResourceNames.includes(name)
    ).map((name) => ({
      name: name,
      meetings: 0,
      meetingHours: "0h",
      occupancy: 0,
      cancelledMeetings: 0,
      cancelledHours: "0h",
      autoBookedMeetings: 0,
      autoBookingHours: "0h",
    }));

    // Combine API data with missing resources
    const allResources = [...transformedResources, ...missingResources];

    console.log("API rooms count:", apiResponse.rooms.length);
    console.log("Transformed resources count:", allResources.length);

    return {
      resources: allResources,
      totalAvailableHours: 0,
    };
  };

  const getApiEndpoint = () => {
    const baseUrl = "https://njs-01.optimuslab.space/pag-rules-processor/api";
    const { start, end } = getDateRange();

    if (selectedFilter === "Daily") {
      // For daily reports, we need hourly data to show hourly breakdown
      return `${baseUrl}/bookings-report-hourly?start=${start}&end=${end}`;
    } else if (selectedFilter === "Custom") {
      // For custom reports, always use daily endpoint
      return `${baseUrl}/bookings-report-daily?start=${start}&end=${end}`;
    } else {
      // For weekly/monthly, we need daily data to show daily breakdown
      return `${baseUrl}/bookings-report-daily?start=${start}&end=${end}`;
    }
  };

  // Get date ranges always in JST timezone
  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate;

    if (selectedFilter === "Daily") {
      if (selectedDate) {
        // For JST day, we need previous day 15:00 UTC to current day 14:59:59 UTC
        const jstSelectedDate = parseUTCToJST(selectedDate);
        const year = jstSelectedDate.getUTCFullYear();
        const month = jstSelectedDate.getUTCMonth();
        const date = jstSelectedDate.getUTCDate();

        // Start: Previous day at 15:00 UTC (JST 00:00)
        startDate = new Date(Date.UTC(year, month, date - 1, 15, 0, 0, 0));
        // End: Current day at 14:59:59 UTC (JST 23:59:59)
        endDate = new Date(Date.UTC(year, month, date, 14, 59, 59, 999));
      } else {
        // For current day
        const nowJST = parseUTCToJST(now);
        const year = nowJST.getUTCFullYear();
        const month = nowJST.getUTCMonth();
        const date = nowJST.getUTCDate();
        startDate = new Date(Date.UTC(year, month, date - 1, 15, 0, 0, 0));
        endDate = new Date(Date.UTC(year, month, date, 14, 59, 59, 999));
      }
    } else if (selectedFilter === "Weekly") {
      if (selectedDate) {
        const jstSelectedDate = parseUTCToJST(selectedDate);
        const year = jstSelectedDate.getUTCFullYear();
        const month = jstSelectedDate.getUTCMonth();
        const date = jstSelectedDate.getUTCDate();

        // Calculate Saturday of the selected week in JST
        startDate = new Date(Date.UTC(year, month, date));
        const dayOfWeek = startDate.getUTCDay();
        // Saturday = 6, so calculate days back to Saturday
        const diffToSaturday = dayOfWeek === 6 ? 0 : -(dayOfWeek + 1);
        startDate.setUTCDate(startDate.getUTCDate() + diffToSaturday);
        startDate.setUTCHours(0, 0, 0, 0);

        // Calculate Friday of the same week (6 days after Saturday)
        endDate = new Date(startDate);
        endDate.setUTCDate(startDate.getUTCDate() + 6);
        endDate.setUTCHours(23, 59, 59, 999);
      } else {
        // Fallback to current week in JST
        const nowJST = parseUTCToJST(now);
        startDate = new Date(nowJST);
        startDate.setUTCDate(startDate.getUTCDate() - startDate.getUTCDay() - 1); // Go to previous Saturday
        startDate.setUTCHours(0, 0, 0, 0);

        endDate = new Date(startDate);
        endDate.setUTCDate(startDate.getUTCDate() + 6);
        endDate.setUTCHours(23, 59, 59, 999);
      }
    } else if (selectedFilter === "Custom") {
      if (customStartDate && customEndDate) {
        // Convert custom dates to JST and set to beginning/end of day in UTC
        const startJST = parseUTCToJST(customStartDate);
        const endJST = parseUTCToJST(customEndDate);

        const startYear = startJST.getUTCFullYear();
        const startMonth = startJST.getUTCMonth();
        const startDateNum = startJST.getUTCDate();

        const endYear = endJST.getUTCFullYear();
        const endMonth = endJST.getUTCMonth();
        const endDateNum = endJST.getUTCDate();

        // Start at 00:00 JST (15:00 previous day UTC)
        startDate = new Date(Date.UTC(startYear, startMonth, startDateNum - 1, 15, 0, 0, 0));
        // End at 23:59:59 JST (14:59:59 same day UTC)
        endDate = new Date(Date.UTC(endYear, endMonth, endDateNum, 14, 59, 59, 999));
      } else {
        // Default to last 30 days in JST
        const nowJST = parseUTCToJST(now);
        endDate = new Date(Date.UTC(nowJST.getUTCFullYear(), nowJST.getUTCMonth(), nowJST.getUTCDate(), 14, 59, 59, 999));

        startDate = new Date(endDate);
        startDate.setUTCDate(startDate.getUTCDate() - 30);
        startDate.setUTCHours(15, 0, 0, 0);
      }
    }

    return {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
    };
  };

  // Get display string for date ranges in JST
  const getDateDisplayString = () => {
    if (selectedFilter === "Daily") {
      if (selectedDate) {
        const jstDate = parseUTCToJST(selectedDate);
        return `${String(jstDate.getUTCDate()).padStart(2, '0')}-${String(jstDate.getUTCMonth() + 1).padStart(2, '0')}`;
      }
      const todayJST = parseUTCToJST(new Date());
      return `${String(todayJST.getUTCDate()).padStart(2, '0')}-${String(todayJST.getUTCMonth() + 1).padStart(2, '0')}`;
    } else if (selectedFilter === "Weekly") {
      if (selectedDate) {
        const jstSelectedDate = parseUTCToJST(selectedDate);
        const startOfWeek = new Date(jstSelectedDate);
        const dayOfWeek = jstSelectedDate.getUTCDay();
        const diffToSaturday = dayOfWeek === 6 ? 0 : -(dayOfWeek + 1);
        startOfWeek.setUTCDate(jstSelectedDate.getUTCDate() + diffToSaturday);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setUTCDate(startOfWeek.getUTCDate() + 6);

        return `${String(startOfWeek.getUTCDate()).padStart(2, '0')}-${String(startOfWeek.getUTCMonth() + 1).padStart(2, '0')} to ${String(endOfWeek.getUTCDate()).padStart(2, '0')}-${String(endOfWeek.getUTCMonth() + 1).padStart(2, '0')}`;
      }
      return "Select a week";
    } else if (selectedFilter === "Custom") {
      if (customStartDate && customEndDate) {
        const startJST = parseUTCToJST(customStartDate);
        const endJST = parseUTCToJST(customEndDate);

        return `${String(startJST.getUTCDate()).padStart(2, "0")}-${String(
          startJST.getUTCMonth() + 1
        ).padStart(2, "0")} to ${String(endJST.getUTCDate()).padStart(
          2,
          "0"
        )}-${String(endJST.getUTCMonth() + 1).padStart(2, "0")}`;
      }
      return "Select date range";
    }
    return "";
  };

  // Fetch data from API
  const fetchBookingsData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const endpoint = getApiEndpoint();
      console.log("Calling endpoint:", endpoint);
      const response = await fetch(endpoint);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiResponse = await response.json();

      setApiData(apiResponse);

      const transformedData = transformApiData(apiResponse);
      console.log("Transformed data:", transformedData);
      console.log(
        "First resource after transform:",
        transformedData.resources[0]
      );

      setData(transformedData);

      console.log("Data state after setting:", transformedData);
      console.log("Selected resource:", selectedResource);
      console.log("Filtered resources:", transformedData.resources);
    } catch (err) {
      console.error("Error fetching bookings data:", err);
      setError(err.message);
      // ... rest of fallback logic
    } finally {
      setIsLoading(false);
    }
  };

  // Apply the selected date range
  const applyDateRange = () => {
    if (reportType === "custom") {
      setStartDate(customStartDate);
      setEndDate(customEndDate);
    }
    fetchBookingsData();
  };

  // Load data on component mount
  useEffect(() => {
    // Only fetch if selectedDate is properly initialized
    if (selectedDate) {
      fetchBookingsData();
    }
  }, [selectedDate]);

  const rowsPerPage = 10;

  const formatHoursAndMinutes = (decimalHours) => {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);

    if (hours === 0) {
      return minutes === 0 ? "0h" : `${minutes}m`;
    } else if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  };

  // Calculate summary cards dynamically from filtered data
  const calculateSummaryCards = () => {
    const totalCancelledHours = filteredResources.reduce((total, resource) => {
      const timeStr = resource.cancelledHours.toString();

      // Parse hours and minutes from formats like "0h 21m", "2h", "1h 30m"
      const hourMatch = timeStr.match(/(\d+)h/);
      const minuteMatch = timeStr.match(/(\d+)m/);

      const hours = hourMatch ? parseInt(hourMatch[1]) : 0;
      const minutes = minuteMatch ? parseInt(minuteMatch[1]) : 0;

      // Convert everything to decimal hours
      return total + hours + minutes / 60;
    }, 0);

    const totalAutoBookings = filteredResources.reduce((total, resource) => {
      return total + resource.autoBookedMeetings;
    }, 0);

    return {
      totalCancelledHours: formatHoursAndMinutes(totalCancelledHours),
      totalAutoBookings: totalAutoBookings,
    };
  };

  // Filter resources based on selected resource
  const getFilteredResources = () => {
    if (selectedResource === "All Resources") {
      return data.resources;
    }
    return data.resources.filter(
      (resource) => resource.name === selectedResource
    );
  };

  // Calculate pagination after function definition
  const filteredResources = getFilteredResources();
  console.log("Total filtered resources:", filteredResources.length);
  console.log(
    "All resource names:",
    filteredResources.map((r) => r.name)
  );
  const totalPages = Math.ceil(filteredResources.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  // Get top 3 rooms by occupancy dynamically from filtered data
  const getTopRooms = () => {
    // Check if top_occupancy_rooms exists and has data
    if (
      apiData?.top_occupancy_rooms &&
      apiData.top_occupancy_rooms.length > 0
    ) {
      return apiData.top_occupancy_rooms.slice(0, 3).map((room) => ({
        name: room.resourceName,
        percentage:
          room.occupancy ||
          parseInt(room.booking_occupancy?.replace("%", "") || "0"),
      }));
    }

    // Fallback to calculating from filtered resources
    return filteredResources
      .sort((a, b) => b.occupancy - a.occupancy)
      .slice(0, 3)
      .map((resource) => ({
        name: resource.name,
        percentage: resource.occupancy,
      }));
  };

  // Generate and download report - fetches fresh data from API
  const handleGenerateReport = () => {
    fetchBookingsData();
  };

  const CustomHourlyTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const hours = Math.floor(value);
      const minutes = Math.round((value - hours) * 60);
      console.log("Hours for value:", value, "=>", hours, "h", minutes, "m");
      let displayText;
      if (hours === 0 && minutes === 0) {
        displayText = "0h";
      } else if (hours === 0) {
        displayText = `${minutes}m`;
      } else if (minutes === 0) {
        displayText = `${hours}h`;
      } else {
        displayText = `${hours}h ${minutes}m`;
      }

      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="font-medium text-gray-800">{label}</p>
          <p style={{ color: payload[0].color }} className="text-sm">
            Booked: {displayText}
          </p>
        </div>
      );
    }
    return null;
  };

  // Export CSV functionality
  const handleExportCSV = () => {
    if (filteredResources.length === 0) return;
  
    // Build report title with date range
    const resourceLabel = selectedResource === "All Resources" ? "All Resources" : selectedResource;
    let dateInfo = "";
    if (selectedFilter === "Daily") {
      const jstDate = parseUTCToJST(selectedDate);
      dateInfo = `${jstDate.getUTCFullYear()}-${String(jstDate.getUTCMonth() + 1).padStart(2, '0')}-${String(jstDate.getUTCDate()).padStart(2, '0')}`;
    } else if (selectedFilter === "Weekly") {
      dateInfo = getDateDisplayString();
    } else if (selectedFilter === "Custom") {
      dateInfo = getDateDisplayString();
    }
    const reportTitle = `Bookings Report - ${selectedFilter} - ${resourceLabel} - ${dateInfo}`;
  
    const csvContent = [
      reportTitle,
      '',
      [
        "Resource Name",
        "Date",
        "Number of Meetings",
        "Meeting Hours",
        "Room Reservation (%)",
        "Cancelled Meetings",
        "Cancelled Hours",
        "Auto Booked Meetings",
        "Auto Booking Hours",
      ].join(","),
      ...filteredResources.map((resource) =>
        [
          `"${resource.name}"`,
          dateInfo,
          resource.meetings,
          resource.meetingHours,
          `${getOccupancyValue(resource.name)}%`,
          resource.cancelledMeetings,
          resource.cancelledHours,
          resource.autoBookedMeetings,
          resource.autoBookingHours,
        ].join(",")
      ),
    ].join("\n");
  
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
  
    const fileDate = dateInfo.replace(/ to /g, '_to_').replace(/ /g, '_');
    a.download = `bookings_report_${selectedFilter.toLowerCase()}_${resourceLabel.replace(/\s+/g, "_")}_${fileDate}.csv`;
  
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleResourceClick = (resource) => {
    setSelectedResource(resource);
  };

  const handleExpandRow = (resourceName) => {
    setExpandedRow(expandedRow === resourceName ? null : resourceName);
  };

  // Add this function near the other helper functions
  const getMaxSelectableDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const jstYesterday = parseUTCToJST(yesterday);
    return `${jstYesterday.getUTCFullYear()}-${String(jstYesterday.getUTCMonth() + 1).padStart(2, '0')}-${String(jstYesterday.getUTCDate()).padStart(2, '0')}`;
  };

  // Hourly occupancy data for the selected resource (ALWAYS IN JST)
  const getHourlyOccupancyData = (resourceName) => {
    console.log("=== DEBUGGING HOURLY DATA ===");
    console.log("Resource name:", resourceName);
    console.log("Selected filter:", selectedFilter);
    console.log("API Data:", apiData);

    if (!apiData?.rooms) {
      console.log("❌ No API data or rooms");
      return [];
    }

    const room = apiData.rooms.find(
      (r) =>
        r.resourceName === resourceName ||
        r.resourceName.replace(/_/g, " ") === resourceName ||
        r.resourceName === resourceName.replace(/ /g, "_")
    );

    console.log("Found room:", room);

    if (!room) {
      console.log("❌ Room not found");
      return [];
    }

    if (selectedFilter === "Daily") {
      console.log("✅ Processing Daily report");

      // Generate working hours 9:00-18:00 JST
      const workingHours = [];
      for (let hour = 9; hour <= 18; hour++) {
        workingHours.push({
          hour: `${hour}:00`,
          occupancy: 0,
        });
      }
      console.log("Initial working hours:", workingHours);

      if (room.hourly_distribution) {
        console.log("✅ Has hourly distribution:", room.hourly_distribution);

        room.hourly_distribution.forEach((item, index) => {
          console.log(`\n--- Processing item ${index} ---`);
          console.log("Item:", item);

          // Parse UTC timestamps
          const startUTC = new Date(item.start_timestamp);
          const endUTC = new Date(item.end_timestamp);

          // Convert to JST (UTC+9)
          const startJST = new Date(startUTC.getTime() + (9 * 60 * 60 * 1000));
          const endJST = new Date(endUTC.getTime() + (9 * 60 * 60 * 1000));

          // Get JST hours (using UTC methods for consistency)
          const startHourJST = startJST.getUTCHours();
          const endHourJST = endJST.getUTCHours();
          const startMinuteJST = startJST.getUTCMinutes();
          const endMinuteJST = endJST.getUTCMinutes();

          console.log(`UTC: ${startUTC.toISOString()} to ${endUTC.toISOString()}`);
          console.log(`JST: ${startHourJST}:${startMinuteJST} to ${endHourJST}:${endMinuteJST}`);

          // Calculate which hours this booking spans in JST (9:00-18:00)
          const startHour = Math.max(9, startHourJST);
          const endHour = Math.min(18, endHourJST);

          for (let h = startHour; h <= endHour; h++) {
            const hourStr = `${h}:00`;
            const hourIndex = workingHours.findIndex((wh) => wh.hour === hourStr);

            if (hourIndex !== -1) {
              let hourDuration = 0;

              if (h === startHourJST && h === endHourJST) {
                // Booking starts and ends in the same hour
                hourDuration = (endMinuteJST - startMinuteJST) / 60;
              } else if (h === startHourJST) {
                // First hour of booking
                hourDuration = (60 - startMinuteJST) / 60;
              } else if (h === endHourJST) {
                // Last hour of booking
                hourDuration = endMinuteJST / 60;
              } else {
                // Full hour in the middle
                hourDuration = 1;
              }

              // Only add if the hour is within working hours (9-18)
              if (h >= 9 && h <= 18) {
                workingHours[hourIndex].occupancy += hourDuration;
                console.log(`Hour ${h}: Added ${hourDuration.toFixed(2)}h, Total: ${workingHours[hourIndex].occupancy.toFixed(2)}h`);
              }
            }
          }
        });
      } else {
        console.log("❌ No hourly distribution data");
      }

      console.log("Final working hours data:", workingHours);
      return workingHours;
    } else {
      console.log("Processing non-daily report");

      if (!room?.daily_distribution) return [];

      // Get the date range from the API call
      const { start, end } = getDateRange();
      const startDate = new Date(start);
      const endDate = new Date(end);

      // Create array of all dates in range IN JST
      const dateRange = [];
      const currentDate = new Date(startDate);

      // Convert start date to JST for display purposes
      currentDate.setTime(currentDate.getTime() + (9 * 60 * 60 * 1000));

      while (currentDate < endDate) {
        // Always use UTC methods to avoid local timezone issues
        const dayStr = `${String(currentDate.getUTCDate()).padStart(2, "0")}-${String(
          currentDate.getUTCMonth() + 1
        ).padStart(2, "0")}`;

        dateRange.push({
          hour: dayStr,
          occupancy: 0,
          date: new Date(currentDate),
        });

        // Move to next day in JST
        currentDate.setUTCDate(currentDate.getUTCDate() + 1);
      }

      console.log("Date range for chart (JST):", dateRange);

      // Process daily distribution data and aggregate by JST date
      room.daily_distribution.forEach((item) => {
        // Handle edge case: if meetings is zero, keep as zero hours
        if (item.meetings === 0) {
          // Parse UTC timestamps to get the JST date for matching
          const startUTC = new Date(item.start_timestamp);

          // Convert to JST (UTC+9) to get the correct date string
          const startJST = new Date(startUTC.getTime() + (9 * 60 * 60 * 1000));

          // Get JST date string
          const dayStr = `${String(startJST.getUTCDate()).padStart(2, "0")}-${String(
            startJST.getUTCMonth() + 1
          ).padStart(2, "0")}`;

          console.log(`No meetings on ${dayStr} (meetings count: 0)`);

          // Find the matching date and explicitly set occupancy to 0
          const dateEntry = dateRange.find((d) => d.hour === dayStr);
          if (dateEntry) {
            // Ensure it's set to 0 (not adding, but setting)
            dateEntry.occupancy = 0;
            console.log(`Set ${dayStr} occupancy to 0h (no meetings)`);
          }
          return; // Skip to next item
        }

        // Parse UTC timestamps for normal case
        const startUTC = new Date(item.start_timestamp);
        const endUTC = new Date(item.end_timestamp);

        // Convert to JST (UTC+9)
        const startJST = new Date(startUTC.getTime() + (9 * 60 * 60 * 1000));

        // Get JST date string
        const dayStr = `${String(startJST.getUTCDate()).padStart(2, "0")}-${String(
          startJST.getUTCMonth() + 1
        ).padStart(2, "0")}`;

        // Calculate duration (use UTC for accuracy)
        const duration = (endUTC - startUTC) / (1000 * 60 * 60);

        console.log(`UTC Time: ${startUTC.toISOString()} -> ${endUTC.toISOString()}`);
        console.log(`JST Date: ${dayStr}, Duration: ${duration.toFixed(2)}h, Meetings: ${item.meetings}`);

        // Find the matching date in our JST date range and add the duration
        const dateEntry = dateRange.find((d) => d.hour === dayStr);
        if (dateEntry) {
          dateEntry.occupancy += duration;
          console.log(`Updated ${dayStr} total: ${dateEntry.occupancy.toFixed(2)}h`);
        } else {
          console.warn(`No JST date match found for ${dayStr} in date range`);
        }
      });

      console.log("Final chart data (JST):", dateRange);

      // Sort by date and return
      return dateRange.sort((a, b) => a.date - b.date);
    }
  };

  // Get color based on occupancy percentage
  const getOccupancyColor = (percentage) => {
    if (percentage >= 80) return "#C71619";
    if (percentage >= 60) return "#D18409";
    if (percentage >= 40) return "#1666CE";
    return "#6DAF38";
  };

  function timeToMinutes(timeStr) {
    // Example input: "19h 46m"
    const hours = parseInt(timeStr.match(/(\d+)h/)?.[1] || 0);
    const minutes = parseInt(timeStr.match(/(\d+)m/)?.[1] || 0);
    return hours * 60 + minutes;
  }

  function calculateBookingValue(meetingHours) {
    const meetingMinutes = timeToMinutes(meetingHours);
    const netMinutes = meetingMinutes;
    let denominator = 0;
    if (selectedFilter === "Daily") {
      denominator = 9 * 60; // 9 hours in minutes
    } else if (selectedFilter === "Weekly") {
      denominator = 7 * 9 * 60; // 7 days of 9 hours each in minutes
    } else {
      // Custom range
      const { start, end } = getDateRange();
      const startDate = new Date(start);
      const endDate = new Date(end);
      const timeDiff = endDate - startDate;
      const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
      denominator = daysDiff * 9 * 60; // days * 9 hours in minutes
    }
    return netMinutes / denominator;
  }

  function getOccupancyValue(resourceName) {
    const resource = data.resources.find((r) => r.name === resourceName);
    const OccupancyVal = Math.round(calculateBookingValue(resource.meetingHours) * 100);
    return Math.min(OccupancyVal, 100);
  }

  const renderExpandedCharts = (resourceName) => {
    if (expandedRow !== resourceName) return null;

    const resource = data.resources.find((r) => r.name === resourceName);
    const hourlyData = getHourlyOccupancyData(resourceName);

    console.log("=== CHART RENDERING ===");
    console.log("Resource:", resource);
    console.log("Chart data:", hourlyData);
    // Pie chart data based on actual resource data


    const OccupancyVal = Math.round(calculateBookingValue(resource.meetingHours) * 100);
    const pieChartData = [
      { name: "Occupied", value: OccupancyVal, color: "#4B97F9" },
      { name: "Available", value: 100 - OccupancyVal, color: "#E5E7EB" },
    ];

    const cancelledHoursValue = parseFloat(
      resource.cancelledHours.replace("h", "")
    );
    const cancelledHoursData = [
      { name: "Cancelled", value: cancelledHoursValue, color: "#D18409" },
      {
        name: "Available",
        value: Math.max(0, 10 - cancelledHoursValue),
        color: "#E5E7EB",
      },
    ];

    const autoBookingHoursValue = parseFloat(
      resource.autoBookingHours.replace("h", "")
    );
    const autoBookingData = [
      { name: "Auto Booked", value: autoBookingHoursValue, color: "#9DC77B" },
      {
        name: "Available",
        value: Math.max(0, 12 - autoBookingHoursValue),
        color: "#E5E7EB",
      },
    ];

    return (
      <tr>
        <td colSpan="11" className="p-0">
          <div className="bg-gray-50 p-6">
            <div className="flex items-start  mb-4">
              <div className="text-sm font-medium text-gray-800">
                {resourceName} | {getDateDisplayString()} (JST)
              </div>
              <div className="text-xs text-red-600 ml-4 whitespace-nowrap">
                * Red indicates concurrent bookings which may exceed 9hr.
              </div>
            </div>
            <div className="flex gap-6 items-start">
              {/* First Pie Chart - Booking Occupancy */}
              <div className="flex-shrink-0" style={{ width: "160px" }}>
                <h4 className="text-xs font-medium text-gray-800 mb-2 uppercase tracking-wide text-center">
                  ROOM RESERVATION
                </h4>
                <div className="relative h-32 flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={60}
                        dataKey="value"
                        startAngle={90}
                        endAngle={450}
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <span
                        className="text-xl font-bold"
                        style={{ color: "#4B97F9" }}
                      >
                        {OccupancyVal}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Second Pie Chart - Cancelled Hours */}
              <div className="flex-shrink-0" style={{ width: "160px" }}>
                <h4 className="text-xs font-medium text-gray-800 mb-2 uppercase tracking-wide text-center">
                  CANCELLED HOURS
                </h4>
                <div className="relative h-32 flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={cancelledHoursData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={60}
                        dataKey="value"
                        startAngle={90}
                        endAngle={450}
                      >
                        {cancelledHoursData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <span
                        className="text-base font-bold"
                        style={{ color: "#D18409" }}
                      >
                        {resource.cancelledHours}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Third Pie Chart - Auto Booking Hours */}
              <div className="flex-shrink-0" style={{ width: "160px" }}>
                <h4 className="text-xs font-medium text-gray-800 mb-2 uppercase tracking-wide text-center">
                  AUTO BOOKING HOURS
                </h4>
                <div className="relative h-32 flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={autoBookingData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={60}
                        dataKey="value"
                        startAngle={90}
                        endAngle={450}
                      >
                        {autoBookingData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <span
                        className="text-base font-bold"
                        style={{ color: "#9DC77B" }}
                      >
                        {resource.autoBookingHours}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bar Chart - Takes remaining space */}
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-medium text-gray-800 mb-2 uppercase tracking-wide text-center">
                  {selectedFilter === "Daily"
                    ? "HOURLY RESERVATION (9:00 - 18:00 JST)"
                    : `DAILY RESERVATION HOURS (${getDateDisplayString()} JST)`}
                </h4>

                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={hourlyData}
                      margin={{ top: 5, right: 15, left: 5, bottom: 25 }}
                      barGap={1}
                      barCategoryGap="8%"
                    >
                      <CartesianGrid
                        strokeDasharray="2 2"
                        stroke="#e5e7eb"
                        horizontal={true}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="hour"
                        axisLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
                        tickLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
                        tick={{ fontSize: 11, fill: "#28292b" }}
                        interval={0}
                        angle={0}
                        textAnchor="middle"
                        height={25}
                      />
                      <YAxis
                        axisLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
                        tickLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
                        tick={{ fontSize: 11, fill: "#28292b" }}
                        domain={[0, "dataMax"]}
                        tickCount={6}
                        width={35}
                        tickFormatter={(value) => {
                          const hours = Math.floor(value);
                          const minutes = Math.round((value - hours) * 60);
                          if (hours === 0 && minutes === 0) return "0";
                          if (minutes === 0) return `${hours}h`;
                          if (hours === 0) return `${minutes}m`;
                          return `${hours}h${minutes}m`;
                        }}
                      />
                      <Tooltip content={<CustomHourlyTooltip />} />
                      <Bar
                        dataKey="occupancy"
                        fill="#1666CE" // Default fallback color
                        radius={[2, 2, 0, 0]}
                        maxBarSize={20}
                      >
                        {hourlyData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.occupancy > 9 ? "#dc2626" : "#1666CE"}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-gray-700 text-center">Reservation (JST)</p>
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const summaryCards = calculateSummaryCards();
  const currentRows = filteredResources.slice(indexOfFirstRow, indexOfLastRow);

  return (
    <div className="w-full">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Error:</strong> {error}. Using fallback data.
        </div>
      )}

      {/* Timezone Info Banner */}
      <div className="bg-blue-50 rounded-lg p-3 mb-4 border border-blue-200">
        <div className="flex items-center">
          <svg
            className="h-4 w-4 text-blue-600 mr-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-blue-800 text-sm">
            All dates and times are displayed in Japan Standard Time (JST, UTC+9)
          </p>
        </div>
      </div>

      {/* Weekly Range Info - Only show for Weekly reports */}
      {selectedFilter === "Weekly" && (
        <div className="bg-blue-50 rounded-lg p-3 mb-4 border border-blue-200">
          <div className="flex items-center">
            <svg
              className="h-4 w-4 text-blue-600 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-blue-800 text-sm">
              Selected Week: {getDateDisplayString()} (JST)
            </p>
          </div>
        </div>
      )}

      {/* Filter Controls Section */}
      <div className="w-full">
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between">
            <div className="flex flex-col md:flex-row md:items-end">
              {/* Report Type */}
              <div className="mb-4 md:mb-0">
                <label className="text-sm text-gray-800 mb-1 block">
                  Report Type
                </label>
                <div className="flex space-x-2">
                  {["Daily", "Weekly", "Custom"].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setSelectedFilter(filter)}
                      className={`px-4 py-2 rounded-md ${selectedFilter === filter
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 text-gray-800"
                        }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>

              <div className="md:ml-12">
                <label className="text-sm text-gray-800 mb-1 block">
                  Resource
                </label>
                <select
                  className="border border-gray-300 rounded-md px-3 py-2"
                  value={selectedResource}
                  onChange={(e) => setSelectedResource(e.target.value)}
                >
                  <option value="All Resources">All Resources</option>
                  {data.resources.map((resource) => (
                    <option key={resource.name} value={resource.name}>
                      {resource.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Selection */}
              <div className="md:ml-12">
                {(selectedFilter === "Daily" ||
                  selectedFilter === "Weekly") && (
                    <div className="mb-4 md:mb-0">
                      <label className="text-sm text-gray-800 mb-1 block">
                        {selectedFilter === "Daily"
                          ? "Select Date (JST)"
                          : "Select Week (JST)"}
                      </label>
                      <div className="flex items-center relative">
                        <input
                          type="date"
                          value={
                            selectedDate
                              ? selectedDate.toISOString().split("T")[0]
                              : ""
                          }
                          max={getMaxSelectableDate()}
                          onChange={(e) =>
                            setSelectedDate(new Date(e.target.value))
                          }
                          className="border border-gray-300 rounded-md px-3 py-2 pl-9"
                        />
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-gray-700"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  )}

                {selectedFilter === "Custom" && (
                  <div className="flex flex-col md:flex-row">
                    <div className="mb-4 md:mb-0 relative">
                      <label className="text-sm text-gray-800 mb-1 block">
                        Start Date (JST)
                      </label>
                      <div className="relative">
                        <input
                          type="date"
                          value={
                            customStartDate
                              ? customStartDate.toISOString().split("T")[0]
                              : ""
                          }
                          max={getMaxSelectableDate()}
                          onChange={(e) =>
                            setCustomStartDate(new Date(e.target.value))
                          }
                          className="border border-gray-300 rounded-md px-3 py-2 pl-9"
                        />
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-gray-700"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="mb-4 md:mb-0 md:ml-6 relative">
                      <label className="text-sm text-gray-800 mb-1 block">
                        End Date (JST)
                      </label>
                      <div className="relative">
                        <input
                          type="date"
                          value={
                            customEndDate
                              ? customEndDate.toISOString().split("T")[0]
                              : ""
                          }
                          max={getMaxSelectableDate()}
                          onChange={(e) =>
                            setCustomEndDate(new Date(e.target.value))
                          }
                          className="border border-gray-300 rounded-md px-3 py-2 pl-9"
                        />
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-gray-700"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 md:mt-0 flex space-x-3">
              <button
                className="px-4 py-2 rounded-md bg-transparent border-[2px] border-blue-600 text-blue-600 font-medium hover:bg-blue-600 hover:text-white"
                onClick={handleExportCSV}
                disabled={filteredResources.length === 0}
              >
                Export CSV
              </button>

              <button
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                onClick={applyDateRange}
                disabled={isLoading}
              >
                {isLoading ? "Loading..." : "Apply"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards - Using Flexbox */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-6">
          {/* First 3 cards - smaller width */}
          <div
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex-shrink-0"
            style={{ width: "200px" }}
          >
            <h3 className="text-[16px] font-medium text-gray-800 ">
              Total hours saved via Cancellation
            </h3>
            <div className="flex items-center justify-center h-full">
              <p className="text-2xl font-semibold text-blue-600">
                {summaryCards.totalCancelledHours}
              </p>
            </div>
          </div>

          <div
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex-shrink-0"
            style={{ width: "200px" }}
          >
            <h3 className="text-[16px] font-medium text-gray-800 ">
              Total Auto Bookings Made
            </h3>
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-2xl font-semibold text-green-600">
                {summaryCards.totalAutoBookings}
              </p>
              <div>
                {" "}
                <p className="text-gray-700 font-medium mt-0.5 mb-4 text-xs">
                  Bookings
                </p>
              </div>
            </div>
          </div>

          {/* Fourth card - wider */}
          <div
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex-1 min-w-0"
            style={{ minWidth: "300px" }}
          >
            <h3 className="text-[16px] font-medium text-gray-800 mb-4">
              Top 3 Meeting Rooms by occupancy
            </h3>
            <div className="space-y-2">
              {getTopRooms().map((room, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-20 text-gray-700 font-medium text-xs">
                    {room.name}
                  </div>
                  <div className="flex-1">
                    <div className="bg-gray-200 rounded-full h-2 w-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500 ease-out"
                        style={{
                          width: `${room.percentage}%`,
                          backgroundColor: getOccupancyColor(room.percentage),
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-sm text-gray-700 w-8 text-right">
                    {room.percentage}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
        {isLoading ? (
          <div className="text-center py-10">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-2">Loading data...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left px-4 py-4 font-semibold text-gray-800 text-[16px]">
                    Resource
                  </th>
                  <th className="text-center px-4 py-4 font-semibold text-gray-800 text-[16px]">
                    Total Number of Meetings
                  </th>
                  <th className="text-center px-4 py-4 font-semibold text-gray-800 text-[16px]">
                    Total Meeting Hours
                  </th>
                  <th className="text-center px-4 py-4 font-semibold text-gray-800 text-[16px]">
                    Total Room Reservation (%)
                  </th>
                  <th className="text-center px-4 py-4 font-semibold text-gray-800 text-[16px]">
                    Cancelled Meetings
                  </th>
                  <th className="text-center px-4 py-4 font-semibold text-gray-800 text-[16px]">
                    Cancelled Hours
                  </th>

                  <th className="text-center px-4 py-4 font-semibold text-gray-800 text-[16px]">
                    Auto Booked Meetings
                  </th>
                  <th className="text-center px-4 py-4 font-semibold text-gray-800 text-[16px]">
                    Auto Booking Hours
                  </th>
                  <th className="text-center px-4 py-4 font-semibold text-gray-800 text-[16px]">
                    Overview
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((resource, index) => (
                  <React.Fragment key={resource.name}>
                    <tr
                      className={`cursor-pointer hover:bg-gray-50 transition-colors ${index === currentRows.length - 1 &&
                          expandedRow !== resource.name
                          ? ""
                          : "border-b border-gray-200"
                        }`}
                      onClick={() => {
                        handleExpandRow(resource.name);
                      }}
                    >
                      <td className="text-left px-4 py-4 font-medium text-gray-900">
                        {resource.name}
                      </td>
                      <td className="text-center px-4 py-4 text-gray-700 font-medium">
                        {resource.meetings}
                      </td>
                      <td className="text-center px-4 py-4 text-gray-700 font-medium">
                        {resource.meetingHours}
                      </td>
                      <td className="text-center px-4 py-4 text-gray-700 font-medium">
                        {getOccupancyValue(resource.name)}%
                      </td>
                      <td className="text-center px-4 py-4 text-gray-700 font-medium">
                        {resource.cancelledMeetings}
                      </td>
                      <td className="text-center px-4 py-4 text-gray-700 font-medium">
                        {resource.cancelledHours}
                      </td>

                      <td className="text-center px-4 py-4 text-gray-700 font-medium">
                        {resource.autoBookedMeetings}
                      </td>
                      <td className="text-center px-4 py-4 text-gray-700 font-medium">
                        {resource.autoBookingHours}
                      </td>
                      <td className="text-center px-4 py-4">
                        <div className="flex justify-center">
                          {expandedRow === resource.name ? (
                            <ChevronUp className="w-5 h-5 text-gray-800" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-800" />
                          )}
                        </div>
                      </td>
                    </tr>
                    {renderExpandedCharts(resource.name)}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredResources.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-800">
              Showing {indexOfFirstRow + 1}–
              {Math.min(indexOfLastRow, filteredResources.length)} of{" "}
              {filteredResources.length} rows
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, idx) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = idx + 1;
                } else if (currentPage <= 3) {
                  pageNum = idx + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + idx;
                } else {
                  pageNum = currentPage - 2 + idx;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-md border text-sm ${currentPage === pageNum
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Bookings;